// src/Anchor.tsx
import { createMemo, splitProps } from "solid-js";
import { Dynamic } from "@corvu/utils/dynamic";
import { mergeRefs } from "@corvu/utils/reactivity";

// src/context.ts
import { createContext, useContext } from "solid-js";
import {
  createKeyedContext,
  useKeyedContext
} from "@corvu/utils/create/keyedContext";
var TooltipContext = createContext();
var createTooltipContext = (contextId) => {
  if (contextId === void 0) return TooltipContext;
  const context = createKeyedContext(
    `tooltip-${contextId}`
  );
  return context;
};
var useTooltipContext = (contextId) => {
  if (contextId === void 0) {
    const context2 = useContext(TooltipContext);
    if (!context2) {
      throw new Error(
        "[corvu]: Tooltip context not found. Make sure to wrap Tooltip components in <Tooltip.Root>"
      );
    }
    return context2;
  }
  const context = useKeyedContext(`tooltip-${contextId}`);
  if (!context) {
    throw new Error(
      `[corvu]: Tooltip context with id "${contextId}" not found. Make sure to wrap Tooltip components in <Tooltip.Root contextId="${contextId}">`
    );
  }
  return context;
};
var InternalTooltipContext = createContext();
var createInternalTooltipContext = (contextId) => {
  if (contextId === void 0) return InternalTooltipContext;
  const context = createKeyedContext(
    `tooltip-internal-${contextId}`
  );
  return context;
};
var useInternalTooltipContext = (contextId) => {
  if (contextId === void 0) {
    const context2 = useContext(InternalTooltipContext);
    if (!context2) {
      throw new Error(
        "[corvu]: Tooltip context not found. Make sure to wrap Tooltip components in <Tooltip.Root>"
      );
    }
    return context2;
  }
  const context = useKeyedContext(
    `tooltip-internal-${contextId}`
  );
  if (!context) {
    throw new Error(
      `[corvu]: Tooltip context with id "${contextId}" not found. Make sure to wrap Tooltip components in <Tooltip.Root contextId="${contextId}">`
    );
  }
  return context;
};

// src/Anchor.tsx
var TooltipAnchor = (props) => {
  const [localProps, otherProps] = splitProps(props, [
    "contextId",
    "ref"
  ]);
  const context = createMemo(
    () => useInternalTooltipContext(localProps.contextId)
  );
  return <Dynamic
    as="div"
    ref={mergeRefs(context().setAnchorRef, localProps.ref)}
    data-corvu-tooltip-anchor=""
    {...otherProps}
  />;
};
var Anchor_default = TooltipAnchor;

// src/Arrow.tsx
import {
  createMemo as createMemo2,
  splitProps as splitProps2
} from "solid-js";
import FloatingArrow from "@corvu/utils/components/FloatingArrow";
import { mergeRefs as mergeRefs2 } from "@corvu/utils/reactivity";
var TooltipArrow = (props) => {
  const [localProps, otherProps] = splitProps2(props, [
    "contextId",
    "ref"
  ]);
  const context = createMemo2(
    () => useInternalTooltipContext(localProps.contextId)
  );
  return <FloatingArrow
    floatingState={context().floatingState()}
    ref={mergeRefs2(context().setArrowRef, localProps.ref)}
    data-corvu-tooltip-arrow=""
    {...otherProps}
  />;
};
var Arrow_default = TooltipArrow;

// src/Content.tsx
import { combineStyle } from "@corvu/utils/dom";
import {
  createMemo as createMemo3,
  Show,
  splitProps as splitProps3
} from "solid-js";
import { Dynamic as Dynamic2 } from "@corvu/utils/dynamic";
import { mergeRefs as mergeRefs3, some } from "@corvu/utils/reactivity";
import { dataIf } from "@corvu/utils";
import Dismissible from "solid-dismissible";
import { getFloatingStyle } from "@corvu/utils/floating";
var TooltipContent = (props) => {
  const [localProps, otherProps] = splitProps3(props, [
    "forceMount",
    "contextId",
    "ref",
    "style"
  ]);
  const context = createMemo3(
    () => useInternalTooltipContext(localProps.contextId)
  );
  const show = () => some(context().open, () => localProps.forceMount, context().contentPresent);
  const enableDismissible = createMemo3(
    () => context().open() || context().contentPresent()
  );
  return <Dismissible
    element={context().contentRef}
    enabled={enableDismissible()}
    dismissibleId={context().tooltipId()}
    onDismiss={() => context().setOpen(false)}
    dismissOnEscapeKeyDown={context().closeOnEscapeKeyDown}
    dismissOnOutsideFocus={false}
    dismissOnOutsidePointer={false}
    noOutsidePointerEvents={false}
    onEscapeKeyDown={context().onEscapeKeyDown}
  >
      {(props2) => <Show when={show()}>
          <Dynamic2
    as="div"
    ref={mergeRefs3(context().setContentRef, localProps.ref)}
    style={combineStyle(
      {
        ...getFloatingStyle({
          strategy: () => context().strategy(),
          floatingState: () => context().floatingState()
        })(),
        "pointer-events": props2.isLastLayer ? "auto" : void 0
      },
      localProps.style
    )}
    id={context().tooltipId()}
    role="tooltip"
    data-closed={dataIf(!context().open())}
    data-open={dataIf(context().open())}
    data-placement={context().floatingState().placement}
    data-corvu-tooltip-content=""
    {...otherProps}
  />
        </Show>}
    </Dismissible>;
};
var Content_default = TooltipContent;

// src/Portal.tsx
import { createMemo as createMemo4, Show as Show2, splitProps as splitProps4 } from "solid-js";
import { Portal } from "solid-js/web";
import { some as some2 } from "@corvu/utils/reactivity";
var TooltipPortal = (props) => {
  const [localProps, otherProps] = splitProps4(props, [
    "forceMount",
    "contextId"
  ]);
  const context = createMemo4(
    () => useInternalTooltipContext(localProps.contextId)
  );
  const show = () => some2(context().open, () => localProps.forceMount, context().contentPresent);
  return <Show2 when={show()}>
      <Portal {...otherProps} />
    </Show2>;
};
var Portal_default = TooltipPortal;

// src/Root.tsx
import { callEventHandler } from "@corvu/utils/dom";
import {
  createMemo as createMemo5,
  createSignal,
  createUniqueId,
  mergeProps,
  untrack
} from "solid-js";
import createControllableSignal from "@corvu/utils/create/controllableSignal";
import createFloating from "@corvu/utils/create/floating";
import createOnce from "@corvu/utils/create/once";
import createPresence from "solid-presence";
import createTooltip from "@corvu/utils/create/tooltip";
import { isFunction } from "@corvu/utils";
var TooltipRoot = (props) => {
  const defaultedProps = mergeProps(
    {
      initialOpen: false,
      placement: "bottom",
      strategy: "absolute",
      floatingOptions: {
        flip: true,
        shift: true
      },
      openDelay: 500,
      closeDelay: 0,
      skipDelayDuration: 0,
      hoverableContent: true,
      group: null,
      openOnFocus: true,
      openOnHover: true,
      closeOnEscapeKeyDown: true,
      closeOnPointerDown: true,
      closeOnScroll: true,
      tooltipId: createUniqueId()
    },
    props
  );
  const [open, setOpen] = createControllableSignal({
    value: () => defaultedProps.open,
    initialValue: defaultedProps.initialOpen,
    onChange: defaultedProps.onOpenChange
  });
  const [anchorRef, setAnchorRef] = createSignal(null);
  const [triggerRef, setTriggerRef] = createSignal(null);
  const [contentRef, setContentRef] = createSignal(null);
  const [arrowRef, setArrowRef] = createSignal(null);
  const { present: contentPresent } = createPresence({
    show: open,
    element: contentRef,
    onStateChange: (state) => {
      if (state === "present") {
        defaultedProps.onContentPresentChange?.(true);
      } else if (state === "hidden") {
        defaultedProps.onContentPresentChange?.(false);
      }
    }
  });
  const floatingState = createFloating({
    enabled: contentPresent,
    floating: contentRef,
    reference: () => anchorRef() ?? triggerRef() ?? null,
    arrow: arrowRef,
    placement: () => defaultedProps.placement,
    strategy: () => defaultedProps.strategy,
    options: () => defaultedProps.floatingOptions
  });
  createTooltip({
    id: () => defaultedProps.tooltipId,
    group: () => defaultedProps.group,
    open,
    close: () => setOpen(false),
    trigger: triggerRef,
    content: contentRef,
    openOnFocus: () => defaultedProps.openOnFocus,
    openOnHover: () => defaultedProps.openOnHover,
    closeOnPointerDown: () => defaultedProps.closeOnPointerDown,
    closeOnScroll: () => defaultedProps.closeOnScroll,
    hoverableContent: () => defaultedProps.hoverableContent,
    openDelay: () => defaultedProps.openDelay,
    closeDelay: () => defaultedProps.closeDelay,
    skipDelayDuration: () => defaultedProps.skipDelayDuration,
    onHover: (event) => {
      if (callEventHandler(
        defaultedProps.onHover,
        event
      ))
        return;
      setOpen(true);
    },
    onLeave: (event) => {
      if (callEventHandler(
        defaultedProps.onLeave,
        event
      ))
        return;
      setOpen(false);
    },
    onFocus: (event) => {
      if (callEventHandler(
        defaultedProps.onFocus,
        event
      ))
        return;
      setOpen(true);
    },
    onBlur: (event) => {
      if (callEventHandler(
        defaultedProps.onBlur,
        event
      ))
        return;
      setOpen(false);
    },
    onPointerDown: (event) => {
      if (callEventHandler(
        defaultedProps.onPointerDown,
        event
      ))
        return;
      setOpen(false);
    },
    onScroll: (event) => {
      if (callEventHandler(
        defaultedProps.onScroll,
        event
      ))
        return;
      setOpen(false);
    }
  });
  const childrenProps = {
    get open() {
      return open();
    },
    setOpen,
    get placement() {
      return defaultedProps.placement;
    },
    get strategy() {
      return defaultedProps.strategy;
    },
    get floatingOptions() {
      return defaultedProps.floatingOptions;
    },
    get floatingState() {
      return floatingState();
    },
    get openDelay() {
      return defaultedProps.openDelay;
    },
    get closeDelay() {
      return defaultedProps.closeDelay;
    },
    get skipDelayDuration() {
      return defaultedProps.skipDelayDuration;
    },
    get hoverableContent() {
      return defaultedProps.hoverableContent;
    },
    get group() {
      return defaultedProps.group;
    },
    get openOnFocus() {
      return defaultedProps.openOnFocus;
    },
    get openOnHover() {
      return defaultedProps.openOnHover;
    },
    get closeOnEscapeKeyDown() {
      return defaultedProps.closeOnEscapeKeyDown;
    },
    get closeOnPointerDown() {
      return defaultedProps.closeOnPointerDown;
    },
    get closeOnScroll() {
      return defaultedProps.closeOnScroll;
    },
    get contentPresent() {
      return contentPresent();
    },
    get contentRef() {
      return contentRef();
    },
    get tooltipId() {
      return defaultedProps.tooltipId;
    }
  };
  const memoizedChildren = createOnce(() => defaultedProps.children);
  const resolveChildren = () => {
    const children = memoizedChildren()();
    if (isFunction(children)) {
      return children(childrenProps);
    }
    return children;
  };
  const memoizedTooltipRoot = createMemo5(() => {
    const TooltipContext2 = createTooltipContext(defaultedProps.contextId);
    const InternalTooltipContext2 = createInternalTooltipContext(
      defaultedProps.contextId
    );
    return untrack(() => <TooltipContext2.Provider
      value={{
        open,
        setOpen,
        placement: () => defaultedProps.placement,
        strategy: () => defaultedProps.strategy,
        floatingOptions: () => defaultedProps.floatingOptions,
        floatingState,
        openDelay: () => defaultedProps.openDelay,
        closeDelay: () => defaultedProps.closeDelay,
        skipDelayDuration: () => defaultedProps.skipDelayDuration,
        hoverableContent: () => defaultedProps.hoverableContent,
        group: () => defaultedProps.group,
        openOnFocus: () => defaultedProps.openOnFocus,
        openOnHover: () => defaultedProps.openOnHover,
        closeOnEscapeKeyDown: () => defaultedProps.closeOnEscapeKeyDown,
        closeOnPointerDown: () => defaultedProps.closeOnPointerDown,
        closeOnScroll: () => defaultedProps.closeOnScroll,
        contentPresent,
        contentRef,
        tooltipId: () => defaultedProps.tooltipId
      }}
    >
        <InternalTooltipContext2.Provider
      value={{
        open,
        setOpen,
        placement: () => defaultedProps.placement,
        strategy: () => defaultedProps.strategy,
        floatingOptions: () => defaultedProps.floatingOptions,
        floatingState,
        openDelay: () => defaultedProps.openDelay,
        closeDelay: () => defaultedProps.closeDelay,
        skipDelayDuration: () => defaultedProps.skipDelayDuration,
        hoverableContent: () => defaultedProps.hoverableContent,
        group: () => defaultedProps.group,
        openOnFocus: () => defaultedProps.openOnFocus,
        openOnHover: () => defaultedProps.openOnHover,
        closeOnEscapeKeyDown: () => defaultedProps.closeOnEscapeKeyDown,
        closeOnPointerDown: () => defaultedProps.closeOnPointerDown,
        closeOnScroll: () => defaultedProps.closeOnScroll,
        contentPresent,
        contentRef,
        tooltipId: () => defaultedProps.tooltipId,
        onFocus: defaultedProps.onFocus,
        onBlur: defaultedProps.onBlur,
        onPointerDown: defaultedProps.onPointerDown,
        onEscapeKeyDown: defaultedProps.onEscapeKeyDown,
        setAnchorRef,
        setTriggerRef,
        setContentRef,
        setArrowRef
      }}
    >
          {untrack(() => resolveChildren())}
        </InternalTooltipContext2.Provider>
      </TooltipContext2.Provider>);
  });
  return memoizedTooltipRoot;
};
var Root_default = TooltipRoot;

// src/Trigger.tsx
import {
  createMemo as createMemo6,
  splitProps as splitProps5
} from "solid-js";
import {
  DynamicButton
} from "@corvu/utils/dynamic";
import { dataIf as dataIf2 } from "@corvu/utils";
import { mergeRefs as mergeRefs4 } from "@corvu/utils/reactivity";
var TooltipTrigger = (props) => {
  const [localProps, otherProps] = splitProps5(props, [
    "contextId",
    "ref"
  ]);
  const context = createMemo6(
    () => useInternalTooltipContext(localProps.contextId)
  );
  return <DynamicButton
    ref={mergeRefs4(context().setTriggerRef, localProps.ref)}
    aria-describedby={context().open() ? context().tooltipId() : void 0}
    aria-expanded={context().open() ? "true" : "false"}
    data-closed={dataIf2(!context().open())}
    data-open={dataIf2(context().open())}
    data-placement={context().open() ? context().floatingState().placement : void 0}
    data-corvu-tooltip-trigger=""
    {...otherProps}
  />;
};
var Trigger_default = TooltipTrigger;

// src/index.ts
var Tooltip = Object.assign(Root_default, {
  Anchor: Anchor_default,
  Trigger: Trigger_default,
  Portal: Portal_default,
  Content: Content_default,
  Arrow: Arrow_default,
  useContext: useTooltipContext
});
var index_default = Tooltip;
export {
  Anchor_default as Anchor,
  Arrow_default as Arrow,
  Content_default as Content,
  Portal_default as Portal,
  Root_default as Root,
  Trigger_default as Trigger,
  index_default as default,
  useTooltipContext as useContext
};
